import type { ProductionLineMonitorResponse } from "../../../shared/production/line-monitor";

export type LineMonitorPhase = "loading" | "ready" | "error" | "unavailable" | "unauthorized" | "forbidden";
export type LineMonitorReading = { runId: string; value: number; measuredAt: string };
export type LineMonitorState = {
  phase: LineMonitorPhase;
  snapshot: ProductionLineMonitorResponse | null;
  reading: LineMonitorReading | null;
  error: string | null;
  receivedAt: number | null;
};
export const initialLineMonitorState: LineMonitorState = {
  phase: "loading", snapshot: null, reading: null, error: null, receivedAt: null,
};

const within = (timestamp: string | null, now: number, tolerance = 10_000) =>
  timestamp !== null && Math.abs(now - Date.parse(timestamp)) <= tolerance;

export function acceptLineMonitorResponse(state: LineMonitorState, response: ProductionLineMonitorResponse, receivedAt: number): LineMonitorState {
  if (!response.available) return { ...state, phase: "unavailable", error: unavailableLineMonitorMessage(response.unavailableReason) };
  if (state.snapshot && Date.parse(response.serverTime) < Date.parse(state.snapshot.serverTime)) return state;
  const now = Date.parse(response.serverTime);
  const sameJob = state.snapshot?.line?.id === response.line?.id && state.reading?.runId === response.run?.id;
  let reading = sameJob ? state.reading : null;
  const measured = response.counter.measuredAt === null ? NaN : Date.parse(response.counter.measuredAt);
  const freshReading = response.counter.quality === "GOOD" && within(response.counter.measuredAt, now)
    && within(response.machine.receivedAt, now);
  const provenOldReading = response.counter.quality === "STALE" && response.run !== null
    && measured >= Date.parse(response.run.startedAt) && measured <= now + 10_000;
  if (response.run && response.counter.runCases !== null && response.counter.measuredAt && (freshReading || provenOldReading)) {
    if (reading && response.counter.runCases < reading.value) return {
      ...state, snapshot: response, phase: "error", error: "İş sayımı geriye gitti. Son doğrulanmış koli miktarı korunuyor.",
    };
    reading = { runId: response.run.id, value: response.counter.runCases, measuredAt: response.counter.measuredAt };
  }
  return { phase: "ready", snapshot: response, reading, error: null, receivedAt };
}

export function unavailableLineMonitorMessage(reason: string | null) {
  if (reason === "DATABASE_UNAVAILABLE") return "Bu sunucu gerçek üretim veritabanına bağlı değil.";
  if (reason === "TAMTAS_LINE_NOT_FOUND") return "Tamtaş Silindir hat kaydı bulunamadı.";
  return "Gerçek hat verisi şu anda alınamıyor.";
}

const qualityLabels: Record<ProductionLineMonitorResponse["counter"]["quality"], string> = {
  GOOD: "Koli ölçümü güncel",
  STALE: "Koli ölçümü gecikti",
  NO_DATA: "Koli ölçümü bekleniyor",
  BASELINE_MISSING: "Bu işin başlangıç sayımı doğrulanamadı",
  COUNTER_RESET: "Sayaç sıfırlandı; iş toplamı doğrulanamıyor",
  GAP_ACROSS_RUN: "Sayım sürekliliği doğrulanamıyor",
};

export function presentLineMonitor(state: LineMonitorState, localNow: number) {
  const snapshot = state.snapshot;
  const serverTime = snapshot ? Date.parse(snapshot.serverTime) : localNow;
  const elapsed = state.receivedAt === null ? 0 : Math.max(0, localNow - state.receivedAt);
  const now = serverTime + elapsed;
  const responding = state.phase === "ready" && elapsed <= 10_000;
  const machineFresh = responding && snapshot !== null && typeof snapshot.machine.running === "boolean"
    && within(snapshot.machine.measuredAt, now) && within(snapshot.machine.receivedAt, now);
  const running = machineFresh ? snapshot?.machine.running ?? null : null;
  const machineState = running === true ? "running" : running === false ? "stopped" : "unknown";
  const counterFresh = responding && snapshot !== null && snapshot.counter.quality === "GOOD"
    && within(snapshot.counter.measuredAt, now) && within(snapshot.machine.receivedAt, now);
  const reading = state.reading?.runId === snapshot?.run?.id ? state.reading : null;
  const quantity = reading?.value ?? null;
  const retained = quantity !== null && !counterFresh;
  const planned = snapshot?.run?.plannedCases ?? null;
  const percent = quantity !== null && planned !== null && planned > 0 ? quantity / planned * 100 : null;
  // Durations use the server snapshot, so a missed stop-end packet or a lost
  // connection never makes the browser invent additional downtime.
  const openAt = snapshot?.downtimeSummary.openStartedAt ?? null;
  const durationNow = serverTime;
  const downtimeMs = snapshot?.run ? snapshot.downtimeSummary.totalMilliseconds : null;
  const openMs = openAt === null ? null : Math.max(0, durationNow - Date.parse(openAt));
  const statusLabel = machineState === "running" ? "Çalışıyor" : machineState === "stopped" ? "Duruşta" : "Durum doğrulanamıyor";
  const connectionLabel = state.phase === "loading" ? "Bağlantı kuruluyor" : !snapshot ? "Veri bekleniyor"
    : !responding ? "Sunucu bağlantısı kontrol edilmeli" : !machineFresh || !counterFresh ? "Ölçüm kontrol edilmeli" : "Ölçümler güncel";
  const quantityLabel = retained ? "Son doğrulanmış iş sayımı" : !snapshot ? "İş sayımı bekleniyor"
    : !snapshot.run ? "Açık iş emri bulunmuyor"
      : quantity === null && snapshot.counter.quality === "GOOD" ? "Bu işin koli sayımı doğrulanamadı" : qualityLabels[snapshot.counter.quality];
  return {
    snapshot, quantity, planned, percent, retained, machineState, statusLabel, connectionLabel,
    quantityLabel, downtimeMs, openMs, durationNow, now, responding, machineFresh, counterFresh,
    remaining: quantity !== null && planned !== null ? Math.max(0, planned - quantity) : null,
    overrun: quantity !== null && planned !== null ? Math.max(0, quantity - planned) : null,
    measuredAt: reading?.measuredAt ?? snapshot?.counter.measuredAt ?? snapshot?.machine.measuredAt ?? null,
  };
}

export const lineMonitorNumber = (value: number | null) => value === null ? "—" : new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(value);
export const lineMonitorTime = (value: string | null, seconds = false) => value === null ? "—" : new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul", hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}),
}).format(new Date(value));
export function lineMonitorDuration(milliseconds: number | null) {
  if (milliseconds === null) return "—";
  const seconds = Math.floor(Math.max(0, milliseconds) / 1000);
  const hours = Math.floor(seconds / 3600);
  return `${hours ? `${hours} sa ` : ""}${Math.floor(seconds % 3600 / 60)} dk ${String(seconds % 60).padStart(2, "0")} sn`;
}
